/**
 * THE SHELL'S STRUCTURED DATA, handed to the page it describes (2026-09-30).
 *
 * public/index.html is served for every address and carries Prest0's structured-data blocks, so a reader that runs
 * no JavaScript (most answer engines' crawlers) learns about Prest0 whatever address it asked for. Once the app runs,
 * each block belongs only on the page it describes. The shell marks the blocks that belong to one page
 * `data-rh="true"`: the head manager (react-helmet-async) then removes them from every page that does not write them.
 * The page each describes writes it again (SHELL_BLOCK_PAGES), and this module is where it gets it: the shell's own
 * copies, read at boot (src/index.tsx calls captureShellStructuredData before the first render, before the head
 * manager's first write removes them). So the shell stays the one copy of their words, and the build's pricing check
 * (scripts/check-public-pricing-sync.mjs), which reads the shell, still checks what those pages say.
 *
 * Organization and WebSite describe the whole site: unmarked, on every page, and never read here.
 * (tests/landing/eachStructuredDataBlockIsOnItsPage.test.tsx)
 */

/** Prest0 itself, described once, by the shell, on every page. */
export const SHELL_ORGANIZATION_ID = 'https://prest0.ai/#organization';

/**
 * How a page's own structured data names Prest0 (as a publisher, a page's main entity…): by pointing to the shell's
 * one Organization, never by describing the company again. A second description is a second company to a search
 * engine, and the ones pages wrote disagreed with the shell and with each other (a lowercase name, another domain,
 * the page's own description as the company's).
 */
export const PREST0_ORGANIZATION_REF = { '@id': SHELL_ORGANIZATION_ID } as const;

/** The shell's blocks that belong to one page, by their `@id`. */
export const SHELL_SOFTWARE_APPLICATION_ID = 'https://prest0.ai/#softwareapplication';
export const SHELL_PRODUCT_ID = 'https://prest0.ai/#product';

/**
 * Which page each of the shell's marked blocks describes: the application on the home page, which describes it; the
 * product and its price list on /pricing, the page that shows the prices. A marked block no page lists (the FAQ,
 * whose questions no page shows) is on no page once the app runs.
 */
export const SHELL_BLOCK_PAGES = {
  '/': [SHELL_SOFTWARE_APPLICATION_ID],
  '/pricing': [SHELL_PRODUCT_ID],
} as const;

type Block = Record<string, unknown>;
const captured = new Map<string, Block>();

/**
 * Read the shell's marked blocks out of the document, once, before the first render. Returns how many were read. A
 * block that does not parse is skipped (a page then writes nothing in its place, which is what it would have said).
 */
export function captureShellStructuredData(doc: Document = document): number {
  captured.clear();
  for (const node of Array.from(doc.head.querySelectorAll('script[type="application/ld+json"][data-rh]'))) {
    try {
      const block = JSON.parse(node.textContent || '') as Block;
      const key = typeof block['@id'] === 'string' ? block['@id'] : String(block['@type'] ?? '');
      if (key) captured.set(key, block);
    } catch {
      /* not JSON: nothing to hand on */
    }
  }
  return captured.size;
}

/** One of the shell's marked blocks, as the shell carries it; undefined when the shell does not (or was not read). */
export function shellStructuredBlock(key: string): Block | undefined {
  return captured.get(key);
}

/** The shell's blocks that belong to a page, as the shell carries them (none that the shell lacks or was not read). */
export function shellBlocksForPage(route: keyof typeof SHELL_BLOCK_PAGES): Block[] {
  return SHELL_BLOCK_PAGES[route].map((id) => captured.get(id)).filter((b): b is Block => b !== undefined);
}
